import StickyTableResponsive from '../../components/common/StickyTableResponsive';
import SortableTh from '../../components/common/SortableTh';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTableSort } from '../../hooks/useTableSort';
import MasterLayout from '../../masterLayout/MasterLayout';
import Breadcrumb from '../../components/Breadcrumb';
import KkpColorBadge from '../../components/kkp/KkpColorBadge';
import KkpPlanningPageActions from '../../components/kkp/KkpPlanningPageActions';
import KkpPlanningEmptyState from '../../components/kkp/KkpPlanningEmptyState';
import { AppFilterBar, AppFilterInput, AppFilterSelect, AppFilterCommercialSelect } from '../../components/filters';
import klubService from '../../services/klubService';
import { ROUTES } from '../../config/routes';
import { goBackOr } from '../../utils/historyBack';
import {
  LISTING_DONNE_LABELS,
  ACCES_CARTES_LABELS,
  CE_ETAPE_FILTER_OPTIONS,
  ceDashboardEtape,
  listingDonneBadgeClass,
  accesCartesBadgeClass,
  SALARIES_RANGE_OPTIONS,
  matchesSalariesRange,
} from '../../utils/klubUtils';
import { AppAlert, AppButton } from '../../components/ui';
import '../../styles/kkp-planning-page.scss';
import '../../styles/kkp-distrib-page.scss';

const TABLE_SORT_COLUMNS = {
  entreprise: { type: 'string', getValue: (row) => row.entreprise },
  commercial: { type: 'string', getValue: (row) => row.commercial?.nom },
  salaries: { type: 'number', getValue: (row) => row.nombre_salaries ?? 0 },
  listing: { type: 'string', getValue: (row) => LISTING_DONNE_LABELS[row.listing_donne] ?? row.listing_donne },
  cartes: { type: 'string', getValue: (row) => ACCES_CARTES_LABELS[row.acces_cartes] ?? row.acces_cartes },
  contact: { type: 'string', getValue: (row) => row.contact },
  ville: { type: 'string', getValue: (row) => row.ville },
};

const paramList = (value) => (value ? [value] : []);

const KlubCePage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [commerciaux, setCommerciaux] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [commercialIds, setCommercialIds] = useState([]);
  const [listingDonne, setListingDonne] = useState([]);
  const [accesCartes, setAccesCartes] = useState([]);
  const [salariesRanges, setSalariesRanges] = useState([]);
  const [etape, setEtape] = useState(() => paramList(searchParams.get('etape')));

  const filteredItems = useMemo(() => items.filter((ce) => {
    if (commercialIds.length > 0) {
      const id = ce.commercial?.id != null ? String(ce.commercial.id) : '';
      if (!commercialIds.includes(id)) return false;
    }
    if (listingDonne.length > 0 && !listingDonne.includes(ce.listing_donne)) return false;
    if (accesCartes.length > 0 && !accesCartes.includes(ce.acces_cartes)) return false;
    if (etape.length > 0 && !etape.includes(ceDashboardEtape(ce))) return false;
    if (!matchesSalariesRange(ce.nombre_salaries, salariesRanges)) return false;
    return true;
  }), [items, commercialIds, listingDonne, accesCartes, etape, salariesRanges]);

  const { sortedRows, sort, toggleSort, getSortIcon } = useTableSort(filteredItems, TABLE_SORT_COLUMNS);

  const hasActiveFilters = Boolean(
    search || commercialIds.length || listingDonne.length || accesCartes.length || salariesRanges.length || etape.length,
  );

  const filteredStats = useMemo(() => {
    const salaries = filteredItems.reduce((sum, ce) => sum + (Number(ce.nombre_salaries) || 0), 0);
    return {
      fiches: filteredItems.length,
      salaries,
      listingOui: filteredItems.filter((ce) => ce.listing_donne === 'oui').length,
      listingRefuse: filteredItems.filter((ce) => ce.listing_donne === 'refuse').length,
      listingAttente: filteredItems.filter((ce) => ce.listing_donne === 'en_attente').length,
      cartesLivrees: filteredItems.filter((ce) => ce.acces_cartes === 'livrees').length,
    };
  }, [filteredItems]);

  const loadData = useCallback(() => {
    setLoading(true);
    setError(null);
    return klubService.listCe({
      search,
      etape: etape.length === 1 ? etape[0] : '',
      limit: 1000,
    })
      .then((res) => {
        setItems(res.ce);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [search, etape]);

  useEffect(() => {
    klubService.listCommerciaux()
      .then(setCommerciaux)
      .catch(() => setCommerciaux([]));
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      loadData();
    }, 300);
    return () => clearTimeout(t);
  }, [loadData]);

  const handleResetFilters = useCallback(() => {
    setSearch('');
    setCommercialIds([]);
    setListingDonne([]);
    setAccesCartes([]);
    setSalariesRanges([]);
    setEtape([]);
    setSearchParams({});
  }, [setSearchParams]);

  const commercialOptions = useMemo(
    () => commerciaux.map((c) => ({ value: String(c.id), label: c.nom })),
    [commerciaux],
  );
  const listingOptions = useMemo(
    () => Object.entries(LISTING_DONNE_LABELS).map(([value, label]) => ({ value, label })),
    [],
  );
  const cartesOptions = useMemo(
    () => Object.entries(ACCES_CARTES_LABELS).map(([value, label]) => ({ value, label })),
    [],
  );

  const filterScope = useMemo(() => {
    if (!hasActiveFilters) return 'Toutes les fiches';
    const parts = [];
    if (search.trim()) parts.push(`Recherche « ${search.trim()} »`);
    if (commercialIds.length) {
      const names = commercialIds
        .map((id) => commercialOptions.find((o) => o.value === id)?.label || id);
      parts.push(names.join(', '));
    }
    if (salariesRanges.length) {
      parts.push(
        salariesRanges
          .map((code) => SALARIES_RANGE_OPTIONS.find((o) => o.value === code)?.label)
          .filter(Boolean)
          .join(', '),
      );
    }
    if (listingDonne.length) {
      parts.push(listingDonne.map((v) => LISTING_DONNE_LABELS[v] || v).join(', '));
    }
    if (accesCartes.length) {
      parts.push(accesCartes.map((v) => ACCES_CARTES_LABELS[v] || v).join(', '));
    }
    if (etape.length) {
      parts.push(etape.map((v) => CE_ETAPE_FILTER_OPTIONS.find((o) => o.value === v)?.label || v).join(', '));
    }
    return parts.join(' · ') || 'Filtres actifs';
  }, [hasActiveFilters, search, commercialIds, commercialOptions, salariesRanges, listingDonne, accesCartes, etape]);

  const subtitle = loading && items.length === 0
    ? 'Chargement…'
    : `${filteredItems.length} fiche${filteredItems.length > 1 ? 's' : ''} CE`;

  const pageActions = useMemo(() => [
    {
      key: 'refresh',
      label: loading ? 'Actualisation…' : 'Actualiser',
      icon: 'mdi:refresh',
      onClick: loadData,
      disabled: loading,
      loading,
    },
    {
      key: 'dashboard',
      label: 'Retour',
      icon: 'mdi:arrow-left',
      onClick: () => goBackOr(navigate, ROUTES.klub.ce),
    },
  ], [loadData, loading, navigate]);

  return (
    <MasterLayout>
      <Breadcrumb
        title="CE Klub"
        subtitle={subtitle}
        actions={<KkpPlanningPageActions actions={pageActions} />}
      />

      {error && (
        <AppAlert variant="danger" className="py-2">
          {error}
          <AppButton size="sm" variant="outline-danger" className="ms-2" onClick={loadData}>
            Réessayer
          </AppButton>
        </AppAlert>
      )}

      <AppFilterBar
        onReset={handleResetFilters}
        resetDisabled={!hasActiveFilters || loading}
      >
        <AppFilterInput
          id="ce-search"
          label="Recherche"
          placeholder="Entreprise, contact…"
          value={search}
          onChange={setSearch}
        />
        <AppFilterCommercialSelect
          id="ce-commercial"
          value={commercialIds}
          onChange={setCommercialIds}
          options={commercialOptions}
        />
        <AppFilterSelect
          id="ce-salaries"
          label="Nb salariés"
          value={salariesRanges}
          onChange={setSalariesRanges}
          options={SALARIES_RANGE_OPTIONS.map(({ value, label }) => ({ value, label }))}
          placeholder="Toutes les tranches"
        />
        <AppFilterSelect
          id="ce-etape"
          label="Étape"
          value={etape}
          onChange={setEtape}
          options={CE_ETAPE_FILTER_OPTIONS}
          multiple={false}
          placeholder="Toutes les étapes"
        />
        <AppFilterSelect
          id="ce-listing"
          label="Listing donné"
          value={listingDonne}
          onChange={setListingDonne}
          options={listingOptions}
          multiple={false}
        />
        <AppFilterSelect
          id="ce-cartes"
          label="Accès cartes"
          value={accesCartes}
          onChange={setAccesCartes}
          options={cartesOptions}
          multiple={false}
        />
      </AppFilterBar>

      <div className="kkp-distrib-filter-stats mb-3">
        <span className="kkp-distrib-filter-stats__scope">{filterScope}</span>
        <div className="kkp-distrib-filter-stats__metrics">
          <div>
            <span>Fiches CE</span>
            <strong>{loading && items.length === 0 ? '—' : filteredStats.fiches.toLocaleString('fr-FR')}</strong>
          </div>
          <div>
            <span>Salariés</span>
            <strong>{loading && items.length === 0 ? '—' : filteredStats.salaries.toLocaleString('fr-FR')}</strong>
          </div>
          <div>
            <span>Listing oui</span>
            <strong>{loading && items.length === 0 ? '—' : filteredStats.listingOui.toLocaleString('fr-FR')}</strong>
          </div>
          <div>
            <span>Listing refus</span>
            <strong>{loading && items.length === 0 ? '—' : filteredStats.listingRefuse.toLocaleString('fr-FR')}</strong>
          </div>
          <div>
            <span>Listing attente</span>
            <strong>{loading && items.length === 0 ? '—' : filteredStats.listingAttente.toLocaleString('fr-FR')}</strong>
          </div>
          <div>
            <span>Cartes livrées</span>
            <strong>{loading && items.length === 0 ? '—' : filteredStats.cartesLivrees.toLocaleString('fr-FR')}</strong>
          </div>
        </div>
      </div>

      <div className="card kkp-planning-results-card">
        <div className="card-body p-0">
          {loading && items.length === 0 ? (
            <KkpPlanningEmptyState loading />
          ) : sortedRows.length === 0 ? (
            <KkpPlanningEmptyState
              message={hasActiveFilters
                ? 'Aucune fiche CE ne correspond à ces filtres.'
                : 'Aucune fiche CE pour le moment.'}
              actions={hasActiveFilters ? [{
                key: 'reset',
                label: 'Réinitialiser les filtres',
                icon: 'mdi:filter-off-outline',
                onClick: handleResetFilters,
              }] : []}
            />
          ) : (
            <StickyTableResponsive>
              <table className="table table-hover mb-0 kkp-iti-list-table">
                <thead>
                  <tr>
                    <SortableTh sortKey="entreprise" label="Entreprise" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
                    <SortableTh sortKey="commercial" label="Commercial" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
                    <SortableTh sortKey="salaries" label="Salariés" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
                    <SortableTh sortKey="listing" label="Listing" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
                    <SortableTh sortKey="cartes" label="Cartes" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
                    <SortableTh sortKey="contact" label="Contact" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
                    <SortableTh sortKey="ville" label="Ville" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
                    <SortableTh label="" sortable={false} />
                  </tr>
                </thead>
                <tbody>
                  {sortedRows.map((ce) => (
                    <tr key={ce.id}>
                      <td className="fw-medium">{ce.entreprise}</td>
                      <td>
                        {ce.commercial?.nom ? (
                          <KkpColorBadge color={ce.commercial.couleur} vivid>
                            {ce.commercial.nom}
                          </KkpColorBadge>
                        ) : (
                          <span className="text-secondary-light">—</span>
                        )}
                      </td>
                      <td>{ce.nombre_salaries ?? '—'}</td>
                      <td>
                        {ce.listing_donne ? (
                          <span className={`badge ${listingDonneBadgeClass(ce.listing_donne)}`}>
                            {LISTING_DONNE_LABELS[ce.listing_donne] ?? ce.listing_donne}
                          </span>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td>
                        {ce.acces_cartes ? (
                          <span className={`badge ${accesCartesBadgeClass(ce.acces_cartes)}`}>
                            {ACCES_CARTES_LABELS[ce.acces_cartes] ?? ce.acces_cartes}
                          </span>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td>
                        <div>{ce.contact ?? '—'}</div>
                        {ce.telephone && (
                          <small className="text-secondary-light d-block">{ce.telephone}</small>
                        )}
                        {ce.email && (
                          <a href={`mailto:${ce.email}`} className="text-primary-600 small text-decoration-none">
                            {ce.email}
                          </a>
                        )}
                      </td>
                      <td>{ce.ville ?? '—'}</td>
                      <td className="text-end">
                        {ce.adherent_id ? (
                          <AppButton
                            as={Link}
                            to={ROUTES.klub.adherentDetail(ce.adherent_id)}
                            size="sm"
                            variant="outline-primary"
                            icon="mdi:account-arrow-right-outline"
                          >
                            Fiche adhérent
                          </AppButton>
                        ) : (
                          <span className="text-secondary-light text-sm">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </StickyTableResponsive>
          )}
        </div>
      </div>
    </MasterLayout>
  );
};

export default KlubCePage;
